import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { Button, Dialog, Field } from '../../ui';
import {
  countTemplateWeeks,
  listTemplates,
  savePlanAsTemplate,
  startPlanFromTemplate,
} from '../../shared/queries';
import type { PlanTemplateRow } from '../../shared/queries';
import { isDuplicateName } from '../../shared/templateNames';
import { supabase } from '../../shared/supabase/client';
import { COPY_NOT_LINK, weekCountLabel } from './templateCopy';

/**
 * The two template gestures that live on a client's plan: saving this plan as a
 * template, and starting this plan from one.
 *
 * They sit together because they are the two halves of one loop — build a plan
 * properly for one person, keep it, start the next person from it — and because
 * they read the same two queries.
 *
 * **Nothing here records or shows where a plan came from.** Once the weeks are
 * copied there is no relationship left to display: no "from template X" on the
 * plan, no way back to it, nothing to re-sync. The confirmation says what was
 * created and says plainly that it is a copy, and then the plan is just her
 * plan. That is not an omission — it is the requirement (`plan-templates`,
 * "Applying a template makes a copy, not a link").
 */
export function PlanTemplateActions({
  planId,
  planTitle,
  clientId,
  clientName,
}: {
  planId: string;
  planTitle: string;
  clientId: string;
  clientName: string;
}) {
  const [saveOpen, setSaveOpen] = useState(false);
  const [startOpen, setStartOpen] = useState(false);
  const [savedName, setSavedName] = useState<string | null>(null);
  const [weeksAdded, setWeeksAdded] = useState<number | null>(null);

  return (
    <div className="flex flex-col gap-3">
      <div className="flex flex-wrap gap-2">
        <Button
          variant="secondary"
          onClick={() => {
            setSavedName(null);
            setSaveOpen(true);
          }}
        >
          Save as template
        </Button>
        <Button
          variant="secondary"
          onClick={() => {
            setWeeksAdded(null);
            setStartOpen(true);
          }}
        >
          Start from a template
        </Button>
      </div>

      {savedName === null ? null : (
        <p role="status" className="text-ink-soft text-sm">
          Saved as a template called “{savedName}”. {clientName}&rsquo;s plan is unchanged, and
          nothing was sent to {clientName}.
        </p>
      )}

      {weeksAdded === null ? null : (
        <p role="status" className="text-ink-soft text-sm">
          {weekCountLabel(weeksAdded)} added as drafts. {COPY_NOT_LINK}
        </p>
      )}

      <SaveAsTemplateDialog
        open={saveOpen}
        onClose={() => {
          setSaveOpen(false);
        }}
        planId={planId}
        planTitle={planTitle}
        clientName={clientName}
        onSaved={(name) => {
          setSaveOpen(false);
          setSavedName(name);
        }}
      />

      <StartFromTemplateDialog
        open={startOpen}
        onClose={() => {
          setStartOpen(false);
        }}
        planId={planId}
        clientId={clientId}
        onStarted={(created) => {
          setStartOpen(false);
          setWeeksAdded(created);
        }}
      />
    </div>
  );
}

/** The database's own limit, repeated here so the form can say so first. */
const NAME_MAX = 80;

function SaveAsTemplateDialog({
  open,
  onClose,
  planId,
  planTitle,
  clientName,
  onSaved,
}: {
  open: boolean;
  onClose: () => void;
  planId: string;
  planTitle: string;
  clientName: string;
  onSaved: (name: string) => void;
}) {
  const queryClient = useQueryClient();

  /**
   * Pre-filled from the plan's title and editable, per the spec.
   *
   * A plan title may be 120 characters and a template name 80, so a long title
   * arrives already cut. Cutting it is kinder than opening the dialog with an
   * error against text she did not type; she sees what will be saved and can
   * edit it before saving.
   */
  const [name, setName] = useState(() => planTitle.trim().slice(0, NAME_MAX));

  const existing = useQuery({
    queryKey: ['templates'],
    queryFn: () => listTemplates(supabase),
    enabled: open,
  });

  const trimmed = name.trim();
  const error =
    trimmed === ''
      ? 'Give the template a name so you can find it again.'
      : trimmed.length > NAME_MAX
        ? `Keep the name to ${String(NAME_MAX)} characters or fewer.`
        : undefined;

  const duplicate = isDuplicateName(
    (existing.data ?? []).map((template) => template.name),
    trimmed,
  );

  const save = useMutation({
    mutationFn: () => savePlanAsTemplate(supabase, planId, trimmed),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ['templates'] });
      await queryClient.invalidateQueries({ queryKey: ['template-week-counts'] });
      onSaved(trimmed);
    },
  });

  return (
    <Dialog open={open} onClose={onClose} title="Save as template">
      <form
        className="mt-4 flex flex-col gap-4"
        onSubmit={(event) => {
          event.preventDefault();
          if (error === undefined) save.mutate();
        }}
      >
        <Field
          label="Template name"
          value={name}
          maxLength={NAME_MAX}
          onChange={(event) => {
            setName(event.target.value);
          }}
          error={error}
        />

        {/*
          Said in words, not carried by an icon or a tooltip: this is the one
          moment she can still decide she wanted the loads. A screen reader
          reads it as the paragraph it is.
        */}
        <div className="text-ink-soft flex flex-col gap-2 text-sm leading-relaxed">
          <p>
            <span className="text-ink font-medium">Carried over:</span> the exercises and their
            order, sets, reps, rest, your cue on each exercise, each session&rsquo;s day and title,
            and the order of the weeks.
          </p>
          <p>
            <span className="text-ink font-medium">Left behind:</span> every load, your session
            notes to {clientName}, the week start dates, and whether a week is published. Those
            belong to {clientName}, not to the programming.
          </p>
        </div>

        {duplicate ? (
          <p role="status" className="text-attention text-sm">
            You already have a template with this name. You can still save it.
          </p>
        ) : null}

        {save.isError ? (
          <p role="alert" className="text-danger text-sm">
            {save.error.message}
          </p>
        ) : null}

        <div className="flex flex-wrap justify-end gap-2">
          <Button variant="ghost" onClick={onClose}>
            Cancel
          </Button>
          {/*
            "Save template", not "Save as template": the action that opens this
            dialog already carries that name, and two controls with one
            accessible name is a sentence a screen reader cannot disambiguate.
          */}
          <Button type="submit" disabled={error !== undefined || save.isPending}>
            {save.isPending ? 'Saving…' : 'Save template'}
          </Button>
        </div>
      </form>
    </Dialog>
  );
}

function StartFromTemplateDialog({
  open,
  onClose,
  planId,
  clientId,
  onStarted,
}: {
  open: boolean;
  onClose: () => void;
  planId: string;
  clientId: string;
  onStarted: (weeksCreated: number) => void;
}) {
  const queryClient = useQueryClient();

  const templates = useQuery({
    queryKey: ['templates'],
    queryFn: () => listTemplates(supabase),
    enabled: open,
  });

  const weekCounts = useQuery({
    queryKey: ['template-week-counts'],
    queryFn: () => countTemplateWeeks(supabase),
    enabled: open,
  });

  const start = useMutation({
    mutationFn: (template: PlanTemplateRow) => startPlanFromTemplate(supabase, template.id, planId),
    onSuccess: async (created) => {
      await queryClient.invalidateQueries({ queryKey: ['weeks', clientId] });
      // `last_used_at` moved, so the list's order did too.
      await queryClient.invalidateQueries({ queryKey: ['templates'] });
      onStarted(created);
    },
  });

  const rows = templates.data ?? [];

  return (
    <Dialog open={open} onClose={onClose} title="Start from a template">
      <p className="text-ink-soft mt-3 text-sm leading-relaxed">{COPY_NOT_LINK}</p>

      {templates.isPending ? <p className="text-ink-muted mt-4 text-sm">Loading…</p> : null}

      {!templates.isPending && rows.length === 0 ? (
        <p className="text-ink-soft mt-4 text-sm leading-relaxed">
          You have no templates yet. Build a plan for someone, then choose “Save as template” on it
          — that is how a template is made.
        </p>
      ) : null}

      {start.isError ? (
        <p role="alert" className="text-danger mt-4 text-sm">
          {start.error.message}
        </p>
      ) : null}

      <ul className="mt-4 flex flex-col gap-2">
        {rows.map((template) => (
          <li
            key={template.id}
            className="border-border flex flex-wrap items-center justify-between gap-3 rounded-[--radius-control] border px-3 py-2"
          >
            <div>
              <p className="text-ink text-sm font-medium">{template.name}</p>
              {/*
                "…" rather than "No weeks" until the count has actually
                arrived. A missing count and a template with no weeks are
                different facts, and the one thing this row exists to tell her
                — how long this template is, before she applies it — must not
                be guessed at while a request is in flight.
              */}
              <p className="text-ink-muted text-xs">
                {weekCounts.isSuccess ? weekCountLabel(weekCounts.data.get(template.id) ?? 0) : '…'}
              </p>
            </div>
            <Button
              variant="secondary"
              disabled={start.isPending}
              onClick={() => {
                start.mutate(template);
              }}
            >
              Use this template
            </Button>
          </li>
        ))}
      </ul>

      <div className="mt-5 flex justify-end">
        <Button variant="ghost" onClick={onClose}>
          Cancel
        </Button>
      </div>
    </Dialog>
  );
}
